import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, Gift, X, Tag } from 'lucide-react';
import useStore from '../store/useStore';
import { translations } from '../i18n/translations';
import { createOrder, getPaymentQR, validateGiftCard, redeemGiftCard } from '../api/api';
import { getVATBreakdown } from '../utils/vatCalculations';

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { language, cart, clearCart, siteSettings } = useStore();
  const t = translations[language];

  const [step, setStep] = useState(1); // 1: Info, 2: Confirmation with QR
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState(null);
  const [qrData, setQrData] = useState(null);

  // Gift card state
  const [giftCardCode, setGiftCardCode] = useState('');
  const [giftCardLoading, setGiftCardLoading] = useState(false);
  const [giftCardError, setGiftCardError] = useState('');
  const [appliedGiftCard, setAppliedGiftCard] = useState(null);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    country: 'belgium',
  });

  const [errors, setErrors] = useState({});

  const subtotalTTC = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const { htva: subtotalHTVA, tva: totalTVA } = getVATBreakdown(subtotalTTC);
  const freeShippingThreshold = siteSettings?.freeShippingThreshold || 75;
  const shippingCost = subtotalTTC >= freeShippingThreshold ? 0 : (siteSettings?.shippingCost || 5.95);
  
  // Calculate discount from gift card
  const giftCardDiscount = appliedGiftCard ? Math.min(appliedGiftCard.balance, subtotalTTC + shippingCost) : 0;
  const total = subtotalTTC + shippingCost - giftCardDiscount;

  // Handle gift card validation
  const handleApplyGiftCard = async () => {
    if (!giftCardCode.trim()) return;
    
    setGiftCardLoading(true);
    setGiftCardError('');
    
    try {
      const result = await validateGiftCard(giftCardCode.trim());
      if (result.valid) {
        setAppliedGiftCard({
          code: giftCardCode.trim().toUpperCase(),
          balance: result.balance,
          original_amount: result.original_amount,
        });
        setGiftCardCode('');
      } else {
        setGiftCardError(result.message || (language === 'fr' ? 'Carte cadeau invalide' : 'Ongeldige cadeaubon'));
      }
    } catch (error) {
      console.error('Gift card validation error:', error);
      setGiftCardError(language === 'fr' ? 'Carte cadeau invalide ou expirée' : 'Ongeldige of verlopen cadeaubon');
    } finally {
      setGiftCardLoading(false);
    }
  };

  const handleRemoveGiftCard = () => {
    setAppliedGiftCard(null);
    setGiftCardError('');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName) newErrors.firstName = 'Required';
    if (!formData.lastName) newErrors.lastName = 'Required';
    if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Valid email required';
    }
    if (!formData.phone) newErrors.phone = 'Required';
    if (!formData.address) newErrors.address = 'Required';
    if (!formData.city) newErrors.city = 'Required';
    if (!formData.postalCode) newErrors.postalCode = 'Required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const orderData = {
        items: cart.map((item) => ({
          product_id: item.product_id,
          name: item.name,
          size: item.size,
          color: item.color,
          quantity: item.quantity,
          price: item.price,
          image: item.image || '', // Include image URL for order history
        })),
        customer_info: {
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          phone: formData.phone,
        },
        shipping_address: {
          address: formData.address,
          city: formData.city,
          postal_code: formData.postalCode,
          country: formData.country,
        },
        subtotal: subtotalTTC,
        shipping_cost: shippingCost,
        gift_card_discount: giftCardDiscount,
        gift_card_code: appliedGiftCard?.code || null,
        total,
      };

      // If gift card is applied, redeem it
      if (appliedGiftCard && giftCardDiscount > 0) {
        try {
          await redeemGiftCard(appliedGiftCard.code, giftCardDiscount);
        } catch (error) {
          console.error('Error redeeming gift card:', error);
          // Continue with order creation even if gift card redemption fails
        }
      }

      const createdOrder = await createOrder(orderData);
      setOrder(createdOrder);

      // Get QR code for payment (only if there's something left to pay)
      if (total > 0) {
        const qr = await getPaymentQR(createdOrder.id);
        setQrData(qr);
      }

      setStep(2);
      clearCart();
    } catch (error) {
      console.error('Error creating order:', error);
      alert(language === 'fr' ? 'Erreur lors de la création de la commande' : 'Fout bij het maken van de bestelling');
    } finally {
      setLoading(false);
    }
  };

  if (cart.length === 0 && !order) {
    navigate('/cart');
    return null;
  }

  // Step 2: Order Confirmation with QR Code
  if (step === 2 && order) {
    return (
      <div className="min-h-screen bg-gray-50" data-testid="checkout-confirmation">
        <div className="max-w-2xl mx-auto px-4 py-16">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {t.checkout.orderConfirmed}
            </h1>
            <p className="text-gray-600 mb-6">{t.checkout.thankYou}</p>

            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-500 mb-1">{t.checkout.orderNumber}</p>
              <p className="text-xl font-bold text-amber-900">{order.order_number}</p>
            </div>

            {/* QR Code Payment */}
            <div className="border-t pt-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                {t.checkout.qrPayment}
              </h2>
              <p className="text-gray-600 mb-6">{t.checkout.qrInstructions}</p>

              {qrData?.qr_code && (
                <div className="flex justify-center mb-6">
                  <img
                    src={qrData.qr_code}
                    alt="Payment QR Code"
                    className="w-64 h-64 border rounded-lg"
                    data-testid="payment-qr-code"
                  />
                </div>
              )}

              <div className="bg-amber-50 rounded-lg p-6 text-left">
                <h3 className="font-semibold text-gray-900 mb-4">
                  {t.checkout.bankDetails}
                </h3>
                <dl className="space-y-2">
                  <div className="flex justify-between">
                    <dt className="text-gray-600">{t.checkout.accountHolder}:</dt>
                    <dd className="font-medium">
                      {qrData?.bank_info?.account_holder || 'EBB-SHOP'}
                    </dd>
                  </div>
                  {qrData?.bank_info?.iban && (
                    <div className="flex justify-between">
                      <dt className="text-gray-600">{t.checkout.iban}:</dt>
                      <dd className="font-medium font-mono">{qrData.bank_info.iban}</dd>
                    </div>
                  )}
                  {qrData?.bank_info?.bic && (
                    <div className="flex justify-between">
                      <dt className="text-gray-600">{t.checkout.bic}:</dt>
                      <dd className="font-medium font-mono">{qrData.bank_info.bic}</dd>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <dt className="text-gray-600">{t.checkout.reference}:</dt>
                    <dd className="font-medium text-amber-700">{qrData?.order_number}</dd>
                  </div>
                  <div className="flex justify-between border-t pt-2 mt-2">
                    <dt className="text-lg font-bold">{t.cart.total}:</dt>
                    <dd className="text-lg font-bold text-amber-900">
                      €{qrData?.total?.toFixed(2) || total.toFixed(2)}
                    </dd>
                  </div>
                </dl>
              </div>

              <p className="text-sm text-gray-500 mt-6">
                {language === 'fr'
                  ? 'Veuillez utiliser la référence de commande comme communication lors du virement.'
                  : 'Gebruik het bestelnummer als mededeling bij de overschrijving.'}
              </p>
            </div>

            <button
              onClick={() => navigate('/')}
              className="mt-8 bg-amber-700 hover:bg-amber-800 text-white px-8 py-3 rounded-lg font-medium transition-colors"
            >
              {t.cart.continueShopping}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Step 1: Checkout Form
  return (
    <div className="min-h-screen bg-gray-50" data-testid="checkout-page">
      {/* Header */}
      <div className="bg-amber-900 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate('/cart')}
            className="flex items-center text-amber-200 hover:text-white mb-4"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            {t.common.back}
          </button>
          <h1 className="text-4xl font-bold text-white">{t.checkout.title}</h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Form */}
            <div className="lg:col-span-2 space-y-6">
              {/* Customer Info */}
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-6">
                  {t.checkout.customerInfo}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t.checkout.firstName} *
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent ${
                        errors.firstName ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t.checkout.lastName} *
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent ${
                        errors.lastName ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t.checkout.email} *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent ${
                        errors.email ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t.checkout.phone} *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent ${
                        errors.phone ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                  </div>
                </div>
              </div>

              {/* Shipping Address */}
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-6">
                  {t.checkout.shippingAddress}
                </h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t.checkout.address} *
                    </label>
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent ${
                        errors.address ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t.checkout.postalCode} *
                      </label>
                      <input
                        type="text"
                        name="postalCode"
                        value={formData.postalCode}
                        onChange={handleChange}
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent ${
                          errors.postalCode ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t.checkout.city} *
                      </label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent ${
                          errors.city ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t.checkout.country} *
                      </label>
                      <select
                        name="country"
                        value={formData.country}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                      >
                        <option value="belgium">{t.checkout.belgium}</option>
                        <option value="france">{t.checkout.france}</option>
                        <option value="netherlands">{t.checkout.netherlands}</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Info */}
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">
                  {t.checkout.paymentMethod}
                </h2>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                      <span className="text-xl">📱</span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{t.checkout.qrPayment}</p>
                      <p className="text-sm text-gray-600">
                        {language === 'fr'
                          ? 'Scannez le QR code avec votre application bancaire'
                          : 'Scan de QR-code met uw bank-app'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow p-6 sticky top-24">
                <h2 className="text-xl font-bold text-gray-900 mb-6">
                  {t.checkout.orderSummary}
                </h2>

                {/* Items */}
                <div className="space-y-4 mb-6 max-h-64 overflow-y-auto">
                  {cart.map((item) => (
                    <div
                      key={`${item.product_id}-${item.size}-${item.color}`}
                      className="flex gap-3"
                    >
                      <img
                        src={item.image || 'https://via.placeholder.com/60'}
                        alt={item.name}
                        className="w-16 h-20 object-cover rounded"
                      />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900 line-clamp-1">
                          {item.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {item.size} | {item.color} | x{item.quantity}
                        </p>
                        <p className="text-sm font-medium text-amber-900">
                          €{(item.price * item.quantity).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Gift Card / Promo Code Section */}
                <div className="border-t pt-4 mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Gift className="inline w-4 h-4 mr-1" />
                    {language === 'fr' ? 'Code promo / Carte cadeau' : 'Promotiecode / Cadeaubon'}
                  </label>
                  
                  {appliedGiftCard ? (
                    <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-3">
                      <div>
                        <span className="font-medium text-green-700">{appliedGiftCard.code}</span>
                        <p className="text-xs text-green-600">
                          {language === 'fr' ? 'Réduction' : 'Korting'}: -€{giftCardDiscount.toFixed(2)}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={handleRemoveGiftCard}
                        className="text-red-500 hover:text-red-700"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={giftCardCode}
                        onChange={(e) => setGiftCardCode(e.target.value.toUpperCase())}
                        placeholder={language === 'fr' ? 'Entrez votre code' : 'Voer uw code in'}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                      />
                      <button
                        type="button"
                        onClick={handleApplyGiftCard}
                        disabled={giftCardLoading || !giftCardCode.trim()}
                        className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:bg-gray-300 text-sm font-medium"
                      >
                        {giftCardLoading ? '...' : (language === 'fr' ? 'Appliquer' : 'Toepassen')}
                      </button>
                    </div>
                  )}
                  
                  {giftCardError && (
                    <p className="text-red-500 text-xs mt-1">{giftCardError}</p>
                  )}
                </div>

                <div className="border-t pt-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">{language === 'fr' ? 'Sous-total HTVA' : 'Subtotaal excl. BTW'}</span>
                    <span className="font-medium">€{subtotalHTVA.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">{language === 'fr' ? 'TVA (21%)' : 'BTW (21%)'}</span>
                    <span className="font-medium">€{totalTVA.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t.cart.subtotal} TTC</span>
                    <span className="font-medium">€{subtotalTTC.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t.cart.shipping}</span>
                    <span className="font-medium">
                      {shippingCost === 0 ? (
                        <span className="text-green-600">{t.cart.freeShipping}</span>
                      ) : (
                        `€${shippingCost.toFixed(2)}`
                      )}
                    </span>
                  </div>
                  {giftCardDiscount > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span className="flex items-center gap-1">
                        <Tag className="w-4 h-4" />
                        {language === 'fr' ? 'Carte cadeau' : 'Cadeaubon'}
                      </span>
                      <span className="font-medium">-€{giftCardDiscount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="border-t pt-3 flex justify-between">
                    <span className="text-lg font-bold">{t.cart.total} TTC</span>
                    <span className="text-lg font-bold text-amber-900">
                      €{total.toFixed(2)}
                    </span>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full mt-6 bg-amber-700 hover:bg-amber-800 disabled:bg-gray-400 text-white py-4 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
                  data-testid="place-order-btn"
                >
                  {loading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      {t.common.loading}
                    </>
                  ) : (
                    t.checkout.placeOrder
                  )}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CheckoutPage;
